import { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import ScrollToTop from "@/components/base/ScrollToTop";
import Breadcrumb from "@/components/base/Breadcrumb";
import Layout from "@/components/feature/Layout";
import { categories } from "@/mocks/categories";
import { getArticlesByCategory, getCategoryBySlug, type Article } from "@/mocks/articles";

const articleImageMap: Record<number, string> = {
  1: "https://readdy.ai/api/search-image?query=Energetic%20modern%20competitive%20gaming%20atmosphere%20warm%20orange%20amber%20tones%20abstract%20geometric%20beginner%20guide%20soft%20glow%20editorial%20style&width=800&height=480&seq=cat-c1-01&orientation=landscape",
  2: "https://readdy.ai/api/search-image?query=Dynamic%20competition%20calendar%20concept%20golden%20trophy%20warm%20amber%20gradient%20energetic%20esports%20atmosphere%20glowing%20markers%20editorial&width=800&height=480&seq=cat-c1-02&orientation=landscape",
  3: "https://readdy.ai/api/search-image?query=Clean%20ranking%20leaderboard%20dashboard%20concept%20warm%20orange%20accents%20abstract%20charts%20glowing%20data%20lines%20modern%20editorial&width=800&height=480&seq=cat-c1-03&orientation=landscape",
  4: "https://readdy.ai/api/search-image?query=Team%20strategy%20collaboration%20concept%20warm%20amber%20tones%20silhouettes%20united%20abstract%20network%20geometric%20connections%20energetic&width=800&height=480&seq=cat-c1-04&orientation=landscape",
  5: "https://readdy.ai/api/search-image?query=Casual%20mobile%20gaming%20lifestyle%20warm%20orange%20accents%20cozy%20soft%20atmosphere%20smartphone%20glowing%20interface%20relaxed%20editorial&width=800&height=480&seq=cat-c1-05&orientation=landscape",
  6: "https://readdy.ai/api/search-image?query=Knowledge%20quiz%20brain%20challenge%20warm%20amber%20glow%20abstract%20question%20marks%20floating%20light%20bulb%20smart%20atmosphere&width=800&height=480&seq=cat-c1-06&orientation=landscape",
  7: "https://readdy.ai/api/search-image?query=Reward%20prize%20showcase%20warm%20golden%20glow%20gift%20boxes%20luxury%20abstract%20geometric%20presentation%20elegant%20editorial&width=800&height=480&seq=cat-c1-07&orientation=landscape",
  8: "https://readdy.ai/api/search-image?query=Professional%20esports%20arena%20elite%20competition%20warm%20orange%20dramatic%20lighting%20champion%20silhouette%20trophy%20epic%20cinematic&width=800&height=480&seq=cat-c1-08&orientation=landscape",
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = slug ? getCategoryBySlug(slug) : undefined;
  const articles = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    if (category) {
      document.title = `${category.name} - 17c每日大赛 | 每天参赛赢好礼`;
      const metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) {
        metaDesc.setAttribute("content", `17c每日大赛${category.name}专区，${category.description}。浏览最新${category.name}攻略和赛事资讯，与万千玩家同台竞技。`);
      }
    }
  }, [category]);

  if (!category) {
    return (
      <Layout>
        <div className="pt-24 pb-16 min-h-screen text-center">
          <h2 className="text-xl font-bold text-foreground-950">分类未找到</h2>
          <Link to="/" className="text-primary-600 mt-4 inline-block">返回首页</Link>
        </div>
      </Layout>
    );
  }

  const breadcrumbItems = [{ label: category.name }];

  return (
    <>
      <ScrollToTop />
      <Layout>
        <div className="pt-24 pb-16 bg-background-50">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="mb-8">
              <Breadcrumb items={breadcrumbItems} />
            </div>

            <div className="mb-12">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 md:w-16 md:h-16 rounded-2xl bg-accent-100 flex items-center justify-center">
                  <i className={`${category.icon} text-2xl text-accent-600`}></i>
                </div>
                <div>
                  <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
                    {category.name}
                  </h1>
                  <p className="text-foreground-600 text-sm md:text-base mt-1">{category.description}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
                    cat.slug === slug
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:bg-background-200 hover:text-foreground-950"
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {articles.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {articles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/article/${article.id}`}
                    className="group rounded-2xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-accent-200/70 transition-all duration-300"
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={articleImageMap[article.id] || articleImageMap[1]}
                        alt={article.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute top-3 left-3">
                        <span className="px-3 py-1 rounded-full bg-accent-500/90 text-background-50 text-xs font-semibold">
                          {article.category}
                        </span>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="text-base font-bold text-foreground-950 mb-2 group-hover:text-primary-600 transition-colors line-clamp-2">
                        {article.title}
                      </h3>
                      <p className="text-sm text-foreground-600 mb-4 line-clamp-2 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span><i className="ri-time-line mr-1"></i>{article.readTime}</span>
                        <span>{article.date}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                  <i className="ri-file-list-3-line text-2xl text-foreground-400"></i>
                </div>
                <p className="text-foreground-500">该分类下暂无文章</p>
              </div>
            )}
          </div>
        </div>
      </Layout>
    </>
  );
}